import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react';
import {Title, Text, Flex, GridContainer, GridItem} from '@priority-software/priority-style-react';
import {onboardingMessageStore} from '../../store/OB_Message_Store';
import {questionnaireStore} from '../../store/OB_Questionnaire_Store';
import {Quiz} from './Quiz';
import {Content} from '../TabsContent/Content';
import {Footer} from '../TabsContent/Footer';
import {Loader, SubmitButton} from '../../components';
import {ErrorScreen} from '../Error/ErrorScreens';
import {priority} from '@priority-software/priority-proto-api/lib';
import texts from '../../texts';
import styles from './styles.module.scss';
import {FinalPage} from './Quiz/FinalPage/FinalPage';
import {sendMonitor} from '../../../../utils/monitoring';
import img from './../../../../images/workspace.png';

const ErrorType = priority.netitems.shared.Message.Type;

type Props = {
    onSubmit: (id: string) => void;
    id: string;
};

export const Questionnaire = observer(({onSubmit, id}: Props) => {
    const {questionCategories, qetQuestionnaireData, isLoading, completed} = questionnaireStore;
    const {message, clearMessage} = onboardingMessageStore;
    const [quizStarted, setQuizStart] = useState(false);

    const runQuiz = useCallback(() => {
        sendMonitor('OnboardingTabsQustionsStart');
        setQuizStart(true);
    }, [setQuizStart]);

    const goToNextTab = useCallback(() => {
        onSubmit(id);
    }, [onSubmit, id]);

    const stopQuiz = useCallback(() => setQuizStart(false), [setQuizStart]);

    useEffect(() => {
        (async () => await qetQuestionnaireData())();
    }, [qetQuestionnaireData]);

    if (isLoading) {
        return <Loader width={80} height={80} componentClasses={styles.loader} />;
    }

    if (message?.message?.MType === ErrorType.Fatal) {
        return (
            <ErrorScreen
                messageData={message}
                clearMessage={clearMessage}
                handler={qetQuestionnaireData}
            />
        );
    }

    if (completed) return <FinalPage goToNextTab={goToNextTab} />;

    if (quizStarted)
        return <Quiz goToNextTab={goToNextTab} stopQuiz={stopQuiz} completed={completed} />;

    return (
        <>
            <Content componentClasses={styles.component}>
                <div className="tabHeader">
                    <Title skin="h2" textColor="primary-500" componentClasses="tabTitle">
                        {texts.general_info_tab_title}
                    </Title>

                    <Text fontWeight="normal" textColor="primary-700">
                        {texts.general_info_tab_subtitle}
                    </Text>
                    <Text tag="p" textColor="primary-700">
                        {texts.general_info_tab_list_title}
                    </Text>
                </div>

                <GridContainer columns={5} padding={1}>
                    <GridItem offset={1} span={2}>
                        <ul className={styles.list}>
                            {questionCategories.map((item, index) => {
                                const itemText = `${index + 1}. ${item.catName}`;
                                return (
                                    <li key={`${item.catID}`}>
                                        <Text skin="paragraph-1" textColor="primary-700">
                                            {itemText}
                                        </Text>
                                    </li>
                                );
                            })}
                        </ul>
                    </GridItem>

                    <GridItem>
                        <img width={341} height={332} src={img} className={styles.img} alt="" />
                    </GridItem>

                    <GridItem offset={1} span={5}></GridItem>
                </GridContainer>
            </Content>

            <Footer>
                <Flex justifyContent="flex-end">
                    <SubmitButton componentClasses={styles.submit} onClick={runQuiz}>
                        {texts.general_info_tab_start}
                    </SubmitButton>
                </Flex>
            </Footer>
        </>
    );
});
